import { PAGE_SX } from '../components/pageLayout';
import React, { useEffect, useState } from "react";
import { Box, LinearProgress, Switch, Typography, useTheme } from "@mui/material";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { styled } from '@mui/system';
import { IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import StorageIcon from '@mui/icons-material/Storage';
import DnsOutlined from '@mui/icons-material/DnsOutlined';
import { AddButton } from '../components/dialogKit';
import WifiOutlinedIcon from '@mui/icons-material/WifiOutlined';
import WifiOffOutlinedIcon from '@mui/icons-material/WifiOffOutlined'
import { format } from 'date-fns';

import Header from "../components/Header";
import PageBanner from '../components/PageBanner';
import CustomColumnMenu from "../components/DataGridCustomColumnMenu";
import DataGridCustomToolbar from "../components/DataGridCustomToolbar";
import GraphCell from "../components/GraphCell";
import { useNavigate } from "react-router-dom";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import FlexBetween from "../components/FlexBetween";
import LoadingOverlay from "../components/LoadingOverlay";
import CustomPagination from "../components/CustomPagination";
import { useAuthContext } from "../context/auth";
import { usePathContext } from "../context/path";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { useFirstServerContext } from "../context/firstServer";
import ToggleButton from "../components/ToggleButton";
import useGridStyles from "../hooks/useGridStyles";

const VPNServers = () => {
  // theme
  const theme = useTheme();
  const { gridSx, gridBorder } = useGridStyles();
  const { auth } = useAuthContext();
  const { setTitle } = usePathContext()
  const { firstServers, dispatch } = useFirstServerContext()
  const axiosPrivate = useAxiosPrivate()
  const [ notFound, setNotFound ] = useState(false)

  const navigate = useNavigate();

  // values for backend
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState({});
  const [search, setSearch] = useState("");

  const apiRef = useGridApiRef();

  // search
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    setTitle("VPNServer Management")
    
    const abortController = new AbortController()
    const getFirstServers = async () => {
      try {
        const response = await axiosPrivate.get('/api/vpnManagement/getfirstservers', {
          params: { page, pageSize, sort: JSON.stringify(sort), search, },
          signal: abortController.signal
        })
        dispatch({type: 'SET_FIRSTSERVER', payload: response.data})
        setNotFound(false)
      } catch (err) {
        // console.log(err)
        setNotFound(true)
      }
    }

    if(auth){
        getFirstServers()
    }

    return () => {
      abortController.abort()
    }
  },[auth, axiosPrivate, dispatch, page, pageSize, search, setTitle, sort]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      window.location.reload(); // Refresh the page
    }, 300000); // 300,000 milliseconds = 5 minutes

    // Cleanup the interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  // Dialog state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedServerId, setSelectedServerId] = useState(null);

  // Customized LinearProgress with specified color using styled from @mui/system
  const ColoredLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 6,
    backgroundColor: theme.palette.grey[300], // Default background color
    '& .MuiLinearProgress-bar': {
      backgroundColor: '#8884d8', // Custom fill color
    },
  }));

  // CenteredContainer for centering items using flexbox
  // const CenteredContainer = styled('div')({
  //   display: 'flex',
  //   alignItems: 'center',
  // });

  const formatServerTarget = (value) => {
    switch (value) {
        case 0:
            return "All";
        case 1:
            return "ET";
        case 2:
            return "DU";
        case 3:
            return "OT";
        default:
            return value;
    }
  };

  // data columns
  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.7,
      renderCell: (params) => (
        <div>
            {params.value.slice(-5)}
        </div>
      ),
    },
    {
      field: "mainIP",
      headerName: "Main IP",
      flex: 1.4
    },   
    {
      field: "countryLong",
      headerName: "Country",
      flex: 1.5
    },
    {
      field: "maxUserCount",
      headerName: "MaxUsers",
      flex: 0.8
    },
    {
      field: "onlineUserCount",
      headerName: "CurUsers",
      flex: 1,      
      renderCell: (params) => {
        const { onlineUserCount, maxUserCount } = params.value;
        const value = Math.round(onlineUserCount);
        const progress = Math.min(onlineUserCount / maxUserCount, 1) * 100;

        return (
          <div style={{ 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center", 
            width: "100%", 
            justifyContent: "center",
            lineHeight: 1.2,
            marginTop: 14,
          }}>
            <div style={{ fontSize: "0.8rem", }}>{value}</div>
            <ColoredLinearProgress 
              variant="determinate" 
              value={progress} 
              style={{ width: "90%", marginTop: 5 }}
            />
          </div>
        );
      }
    },
    {
      field: "max_speed",
      headerName: "Max Speed",
      flex: 1.1,
      valueFormatter: (params) => {        
        return `${params} Mbps`;
      }
    },
    {
      field: "logs",
      headerName: "Network Speed (Mbps)",
      flex: 2.8,
      renderCell: (params) => <GraphCell data={params.value} float={3}/>
    },
    {
      field: "bandwidth",
      headerName: "BandWidth",
      valueFormatter: (params) => {
        return `${params.toFixed(1)} GB`;
      }
    },
    {
      field: "server_provider",
      headerName: "Provider",
      flex: 1.2
    },
    {
      field: "server_price",
      headerName: "Price($)",
      flex: 0.8
    },
    {
      field: "use_domain",
      headerName: "Use Domain",
      valueFormatter: (params) => `${params === 1 ? 'Yes' : 'No'}`,
      flex: 1,
    },
    {
      field: "server_type", //1: TLS, 2: QUIC
      headerName: "Type",
      flex: 0.5,
      renderCell: (params) => (
        <div style={{ color: '#FF2727' }}>
          {params.value === 1 ? 'TLS' : 'QUIC'}
        </div>
      ),
    },
    {
      field: "server_target",
      headerName: "Target",
      flex: 0.3,
      valueFormatter: (params) => formatServerTarget(params.value)
    },
    {
      field: "ping_status",
      headerName: "Conn",
      flex: 0.5,
      renderCell: (params) => (
        params.value === 1 ? <WifiOutlinedIcon color="success" /> : <WifiOffOutlinedIcon color="error" />
      ),
    },    
    {
      field: "created_at",
      headerName: "Create At",
      flex: 1.6,
      renderCell: (params) => {
        return format(new Date(params.value), 'yyyy-MM-dd HH:mm:ss');
      }
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.4,

      renderCell: (params) => (
        <Switch
          checked={Boolean(params.value)}
          onChange={() =>
            handleServerStatusChange(params.row.id, !Boolean(params.value))
          }
          size="small"
          sx={{
            width: 40,
            height: 24,
            padding: 0,

            "& .MuiSwitch-switchBase": {
              padding: "3px",

              "&.Mui-checked": {
                transform: "translateX(18px)",

                color: theme.palette.getContrastText(
                  theme.palette.secondary.main
                ),

                "& + .MuiSwitch-track": {
                  backgroundColor: theme.palette.secondary.main,
                  opacity: 1,
                },
              },
            },

            "& .MuiSwitch-thumb": {
              width: 18,
              height: 18,
              boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
            },

            "& .MuiSwitch-track": {
              borderRadius: 12,
              backgroundColor: theme.palette.grey[600],
              opacity: 1,
            },
          }}
        />
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 130,
      renderCell: (params) => (
        <div>
          <Tooltip title="List IPs">
            <IconButton
              color="secondary"
              size="small"
              style={{ marginRight: 8 }}
              onClick={() => handleOpenIPs(params.row.id)}
            >
              <StorageIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Server">
            <IconButton
              color="secondary"
              size="small"
              style={{ marginRight: 8 }}
              onClick={() => handleEdit(params.row.id)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Server">
            <IconButton
              color="secondary"
              size="small"
              onClick={() => handleDelete(params.row.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </div>
      ),
    }
  ];

  const handleOpenIPs = (serverId) => {
    // Logic for editing the row with the given id
    // console.log(`List row with id: ${serverId}`);
    navigate(`/vpnserverips/${serverId}`);
  };

  const handleEdit = (serverId) => {
    // Logic for editing the row with the given id
    // console.log(`Edit row with id: ${id}`);
    const selectedServer = firstServers.combinedData.find(server => server.id === serverId);
    navigate(`/editserver/${serverId}`, { state: { server: selectedServer } });
  };
  
  const handleDelete = (id) => {
    // Logic for deleting the row with the given id
    //console.log(`Delete row with id: ${id}`);
    handleOpenDeleteDialog(id);
  };

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (serverId) => {
    setSelectedServerId(serverId);
    setOpenDeleteDialog(true);
  };

  // Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedServerId(null);
  };

  // Handle delete server
  const handleDeleteServer = async () => {
    try {
      const response = await axiosPrivate.delete(`/api/vpnManagement/deletefirstserver/${selectedServerId}`, {
        params: {serverId: selectedServerId},
      });
      dispatch({ type: 'DELETE_FIRSTSERVER', payload: response.data });
      // Refetch or update state after deletion
      setSearch(search); // This will refetch data with the current search term
      handleCloseDeleteDialog();
    } catch (error) {
      console.error("Failed to delete the server:", error);
    }
  };

  const handleAddServersClick = () => {
    // console.log("Add Servers button clicked!");
    navigate("/editserver/0");
  };

  const handleServerStatusChange = async ( serverId, newStatus) => {
    try {
      const response = await axiosPrivate.patch('/api/vpnManagement/updatefirstserver', {
        params: { 
          page, pageSize, sort: JSON.stringify(sort), search, 
          serverId: serverId, newStatus: newStatus 
        },      
      });
      dispatch({ type: 'UPDATE_FIRSTSERVER', payload: response.data });
      // console.log('Server status updated successfully');
      // Optionally update the UI or state with the new status
    } catch (error) {
      console.error('Failed to update server status:', error);
      // Handle error (e.g., show error message to user)
    }
  }

  // loader
  if (!firstServers) {
    return (
      <Typography variant="h5" mt="20%" textAlign="center">
        Loading...
      </Typography>
    );
  }

  return (
    <Box sx={PAGE_SX}>
      <FlexBetween>
        {/* Header */}
        <Header title="VPN SERVERS" subtitle="VPN servers management" />
        <PageBanner />

        {/* Content */}
        <FlexBetween>
          <Box>
            {/* Opens the server editor page (same gold Add button as the other pages) */}
            <AddButton
              label="Add Servers"
              icon={<DnsOutlined />}
              onClick={handleAddServersClick}
            />
          </Box>          
        </FlexBetween>
      </FlexBetween>

      <Box
        mt="1rem"
        flex={1}
        minHeight="400px"
        backgroundColor={theme.palette.background.alt}
        border={gridBorder}
        borderRadius="0.55rem"
        overflow="hidden"
      >
        {/* Grid Table */}
        <DataGrid
          loading={!firstServers}
          getRowId={(row) => row.id}
          rows={(firstServers && firstServers.combinedData) || []}
          columns={columns}
          rowCount={(firstServers && firstServers.total) || 0}
          rowsPerPageOptions={[20, 50, 100]}
          pagination
          page={page}
          pageSize={pageSize}
          paginationMode="server"
          sortingMode="server"
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          onSortModelChange={(newSortModel) => setSort(...newSortModel)}
          components={{
            Toolbar: DataGridCustomToolbar,
            ColumnMenu: CustomColumnMenu,
            Pagination: CustomPagination, // Use the custom pagination component
          }}
          componentsProps={{
            pagination: { 
              apiRef, 
              count: firstServers?.total, 
              page, 
              pageSize, 
              onPageChange: setPage,
              onPageSizeChange: setPageSize,
            },
            toolbar: { searchInput, setSearchInput, setSearch },
          }}
          sx={gridSx}
        />
      </Box>

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        title="Delete VPN server?"
        handleClose={handleCloseDeleteDialog}
        handleDelete={handleDeleteServer}
      />      

      {/* <LoadingOverlay isLoading={isUpdatingFirebase} showContent='Updating...' /> */}
    </Box>
  );
};

export default VPNServers;